import { useQuery } from "@tanstack/react-query";

import axiosClient from "./apiClient";

/**
 * The organization's people, and which sensors each may reach.
 *
 * Two `cts_api` surfaces, kept separate here because they are separate concerns
 * and separate reads:
 *
 *   - `GET Auth0/v1/tenants/current/members` -- who is in the tenant and what
 *     roles they hold. Backed by Auth0, not DynamoDB.
 *   - `GET Auth0/v1/tenants/current/access-grants` -- which properties and sensors
 *     each of them may reach (REQ-ACL-10). One row per person who has a grant;
 *     somebody with no grant has no row, which is a meaningful state and not an
 *     empty one.
 *
 * Both are admin-only server-side, so a viewer without `ManagePeople` gets a 403
 * rather than an empty list.
 */

export type MemberRole = {
  /** Auth0's role id. What the role endpoints take. */
  id?: string;
  /** Auth0's role name. What the catalogue is matched on -- never the label. */
  name?: string;
  /** This role's own tier, or "None" where the API does not map it. */
  tier?: string;
  /** What to call this role, or empty where the API does not map it. */
  label?: string;
};

export type Member = {
  userId: string;
  email?: string | null;
  name?: string | null;
  picture?: string | null;
  /** Auth0 role objects. Names are matched against the role catalogue. */
  roles?: MemberRole[] | null;
  /**
   * The folded tier: "Administrator" | "Manager" | "User" | "None".
   *
   * Folded by the API (`RoleCapabilities.TierOf`, the maximum of the roles held),
   * never here. A client that folded it itself would be a second definition of
   * what a set of roles adds up to, and the two could disagree.
   */
  tier?: string | null;

  /**
   * True when this row is an invitation nobody has accepted yet.
   *
   * A pending row has **no `userId`** — Auth0 creates no account until the
   * invitation is accepted — so everything keyed on one cannot apply: the
   * detail page, the role endpoints, the access grant. Opening such a row was
   * what made an invited person look like a broken member.
   */
  pending?: boolean;

  /** The invitation's id, for withdrawing it. Absent on a real member. */
  invitationId?: string | null;

  /** When the invitation stops working, ISO-8601. Absent on a real member. */
  invitationExpiresAt?: string | null;

  /**
   * What to call this person's tier.
   *
   * From the API because the word depends on the tenant: a household says
   * "Member" where an organization says "User", for the same tier.
   */
  tierLabel?: string | null;

  /**
   * Entitlements held alongside the tier, already labelled.
   *
   * Separate from the tier so they do not render as a second one. Every family app
   * user holds `StoveGuardian-User` beside `EndUser`, which is what made one person
   * look like two roles on the People page.
   */
  entitlements?: string[] | null;

  /**
   * "family" | "organization".
   *
   * `memberType`, not `member_type`. `MemberDto` takes no `[JsonPropertyName]`, so
   * every field on this endpoint is camel-cased from the C# property name -- the
   * members endpoint is the one place in cts_api that is not snake_case, and its
   * own remarks record that deliberately. This read was declared snake_case, which
   * nothing noticed because a missing field is `undefined` rather than an error.
   * The dev fixture carried the same wrong spelling, so it would have agreed.
   * Pinned now by `mockApi.ts` asserting `satisfies Member[]`.
   */
  memberType?: string | null;
};

/**
 * One person's access grant. Mirrors `AccessGrantDto` in cts_api.
 *
 * `all_properties` means the whole tenant including properties added later, which
 * is NOT the same as listing today's properties in `group_ids`. When it is true the
 * server clears the other three, so one grant cannot have two readings.
 */
export type AccessGrant = {
  user_id?: string | null;
  all_properties: boolean;
  group_ids: string[];
  include_sensors: string[];
  /** Withheld even inside a granted property, and beats an explicit include. */
  exclude_sensors: string[];
  granted_by?: string | null;
  created_datetime?: string | null;
  modified_datetime?: string | null;
};

/**
 * The paged envelope every cts_api list endpoint uses.
 */
type Paged<T> = { data?: T[] | null; nextPageToken?: string | null; size?: number };

export async function getMembers(): Promise<Member[]> {
  const res = await axiosClient.get<Paged<Member>>(
    "Auth0/v1/tenants/current/members",
    { params: { pageSize: 50 } }
  );
  return res.data?.data ?? [];
}

export async function getAccessGrants(): Promise<AccessGrant[]> {
  const res = await axiosClient.get<AccessGrant[]>(
    "Auth0/v1/tenants/current/access-grants"
  );
  return Array.isArray(res.data) ? res.data : [];
}

export const MEMBERS_QUERY_KEY = ["people", "members"] as const;
export const GRANTS_QUERY_KEY = ["people", "grants"] as const;

/**
 * The tenant's members.
 *
 * `staleTime` is deliberately short rather than Infinity: an admin who has just
 * invited somebody expects to see them, and this list is the screen they are
 * looking at while that happens.
 *
 * A caution carried from the API: this endpoint still reads each member's roles
 * individually, so it costs one Auth0 request per person rather than one per
 * tenant. The per-member `Task.Delay(500)` that used to pace that at 2
 * requests/second is GONE -- cts_api #86 replaced it with `MemberFanOut`, four
 * reads in flight at a time backing off only on a rate-limit refusal -- so a
 * twenty-person tenant no longer costs ten seconds of deliberate sleep. It is
 * still a fan-out and still the slowest read behind this page, which is why the
 * loading state says so rather than looking stuck.
 */
export function useMembers() {
  return useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: getMembers,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Every access grant in the tenant, keyed by user id for the people list.
 *
 * Separate from the member list so a slow or failing member read does not take
 * the grant column with it, and so the two can be invalidated independently
 * after an edit.
 */
export function useAccessGrants() {
  return useQuery({
    queryKey: GRANTS_QUERY_KEY,
    queryFn: getAccessGrants,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Describes a grant in a few words, for a table cell.
 *
 * Exported, and the only place this phrasing lives, so the list and any future
 * detail view cannot describe the same grant differently. `undefined` means the
 * person has no grant row at all -- which, once their tenant enforces scope, means
 * no access rather than unrestricted access.
 */
export function describeGrant(
  grant: AccessGrant | undefined,
  propertyCount?: number
): string {
  if (!grant) return "No access";
  if (grant.all_properties) return "All properties";

  const properties = grant.group_ids?.length ?? 0;
  const added = grant.include_sensors?.length ?? 0;
  const withheld = grant.exclude_sensors?.length ?? 0;

  const parts: string[] = [];
  parts.push(
    propertyCount === undefined
      ? `${properties} ${properties === 1 ? "property" : "properties"}`
      : `${properties} of ${propertyCount} properties`
  );
  if (added > 0) parts.push(`+${added} sensor${added === 1 ? "" : "s"}`);
  if (withheld > 0) parts.push(`−${withheld} withheld`);

  if (properties === 0 && added === 0) return "No access";

  return parts.join(", ");
}

/**
 * Whether a grant conveys anything at all.
 *
 * Exported beside `describeGrant` and read by the dot that sits next to it, because
 * they were two definitions of the same statement and they disagreed. The dot asked
 * `all_properties || group_ids.length > 0`, so a grant holding only per-sensor
 * includes rendered as muted -- "nothing" -- beside text reading "0 properties, +1
 * sensor". `people.test.ts` already pins that direction for the words, as the
 * dangerous one: telling an admin somebody has nothing when they can reach a stove.
 *
 * The draft-side equivalent is `grantDraft.grantsNothing`, which answers the same
 * question about the editable shape; this one answers it about the saved row.
 */
export function grantConveysSomething(grant: AccessGrant | undefined): boolean {
  if (!grant) return false;
  if (grant.all_properties) return true;

  return (grant.group_ids?.length ?? 0) > 0 || (grant.include_sensors?.length ?? 0) > 0;
}

/**
 * What the API said went wrong, if it said anything useful.
 *
 * The access and role endpoints refuse with messages written to be read by the
 * person refused -- "A User reaches one sensor rather than a whole property" --
 * and swallowing those into a generic "we couldn't save it" throws away the only
 * part that tells an admin what to do instead. That is not hypothetical: the
 * single-sensor rule (REQ-ACL-15) and the delegation guard (REQ-ACL-14) both
 * refuse requests that look perfectly reasonable from the screen, so without the
 * server's wording the refusal is unactionable.
 *
 * Falls back to the caller's own wording when the body carries nothing readable,
 * because a transport failure has no server message to show.
 */
export function apiMessage(error: unknown, fallback: string): string {
  const body = (error as { response?: { data?: unknown } })?.response?.data;

  if (typeof body === "string" && body.trim()) return body.trim();

  const message = (body as { message?: unknown } | undefined)?.message;

  return typeof message === "string" && message.trim() ? message.trim() : fallback;
}

/**
 * Writes a member's access grant, replacing it whole.
 *
 * A full replace matches the server (REQ-ACL-10): a grant is small and always
 * edited entirely, so partial semantics would give two ways to say "no properties"
 * that no caller wants to tell apart.
 */
export async function putAccessGrant(
  userId: string,
  body: {
    all_properties: boolean;
    group_ids: string[];
    include_sensors: string[];
    exclude_sensors: string[];
  }
): Promise<AccessGrant> {
  const res = await axiosClient.put<AccessGrant>(
    `Auth0/v1/tenants/current/members/${encodeURIComponent(userId)}/access-grant`,
    body
  );
  return res.data;
}

/** Revokes a member's grant. Idempotent server-side. */
export async function deleteAccessGrant(userId: string): Promise<void> {
  await axiosClient.post(
    `Auth0/v1/tenants/current/members/${encodeURIComponent(userId)}/access-grant/delete`
  );
}

/**
 * Removes somebody from the caller's tenant.
 *
 * Takes away their membership, not their Auth0 account: they keep the login and
 * lose this organization. That is the only thing this endpoint can do, and it is
 * the right one -- an organization admin removing a colleague should not be able
 * to delete a person's identity, which may be a login they use elsewhere.
 *
 * Their access grant is NOT cleaned up, deliberately. A grant is keyed on tenant
 * and user, so it confers nothing once the membership is gone, and keeping it means
 * re-adding somebody restores what they had rather than silently giving them
 * nothing. REQ-ACL-3 already states that the resolver tolerates a dangling id.
 */
export async function removeMember(userId: string): Promise<void> {
  await axiosClient.post("Auth0/v1/tenants/current/members/delete", [userId]);
}

/**
 * Adds roles to a member.
 *
 * The API takes Auth0 role IDs, which is why the role catalogue exists: nothing
 * else tells a client what `rol_…` to send, and the phone app hardcodes four
 * because of it.
 */
export async function addMemberRoles(userId: string, roleIds: string[]): Promise<void> {
  await axiosClient.post("Auth0/v1/tenants/current/members/roles", [
    { user_id: userId, role_ids: roleIds },
  ]);
}

/** Removes roles from a member. */
export async function removeMemberRoles(
  userId: string,
  roleIds: string[]
): Promise<void> {
  await axiosClient.post("Auth0/v1/tenants/current/members/roles/delete", [
    { user_id: userId, role_ids: roleIds },
  ]);
}

/**
 * Invites somebody into the viewer's own tenant.
 *
 * One call for both tenant kinds. The API dispatches on the tenant's
 * `entry_type` — an organization invitation and a family one are different Auth0
 * mechanisms — so this does NOT branch, and deliberately sends no tenant or
 * organization id: the API takes it from the caller's own token (REQ-AUTH0-4).
 * The two older endpoints take it in the route, which made "invite into my
 * tenant" a request that names a tenant.
 *
 * `role_ids`, not names: the dashboard holds no role-name literal, and the
 * catalogue is where the ids come from. An empty list is a member with no role
 * yet, which is a real state — the tier can be set afterwards.
 *
 * **No sensor scope is sent, because none can be.** An access grant is keyed on
 * `user_id`, and the invitee has none until they accept, so there is nothing to
 * key a grant to. A User's single sensor has to be set after acceptance, and the
 * modal says so rather than offering a picker that could not be saved.
 */
export async function sendInvitation(
  email: string,
  roleIds: string[]
): Promise<InvitationSent> {
  const res = await axiosClient.post<{ data?: InvitationSent } & InvitationSent>(
    "Auth0/v1/tenants/current/invitations",
    { email, role_ids: roleIds }
  );

  // This endpoint answers through ApiResponse.Ok, which wraps the payload as
  // { status, data, message } -- unlike putAccessGrant beside it, which returns
  // its DTO bare. Returning res.data unwrapped gave an object whose `email` was
  // undefined, and the modal keyed its confirmation on that: the invitation sent
  // and the dialog sat there as though nothing had happened.
  return res.data?.data ?? res.data;
}

/**
 * Chooses the access somebody will have, before they have accepted.
 *
 * Stored against the INVITATION, because a grant is keyed on a user id and an
 * invitee has none until Auth0 creates their account. The API moves it onto that
 * account at their first sign-in (REQ-ACL-23), and withdrawing the invitation
 * takes it with it.
 *
 * Same body as `putAccessGrant`, and the same rules: a User invited with one
 * sensor is refused a whole property here exactly as they would be afterwards.
 */
export async function putPendingAccessGrant(
  invitationId: string,
  body: {
    all_properties: boolean;
    group_ids: string[];
    include_sensors: string[];
    exclude_sensors: string[];
  }
): Promise<AccessGrant> {
  const res = await axiosClient.put<{ data?: AccessGrant } & AccessGrant>(
    `Auth0/v1/tenants/current/invitations/${encodeURIComponent(
      invitationId
    )}/access-grant`,
    body
  );

  // Unwrapped the same way sendInvitation is: whether an endpoint wraps its
  // payload is a per-endpoint fact here, and reading the envelope as the payload
  // is what left the invite dialog showing no confirmation at all.
  return res.data?.data ?? res.data;
}

/**
 * The key a pending grant is stored under, from the invited address.
 *
 * Mirrors `PendingGrantKey` in the API. Duplicated deliberately rather than
 * derived: the dashboard needs it only to MATCH a row it has already been given,
 * and the alternative is another endpoint whose whole job is to echo a string
 * back. If the two ever disagree the symptom is a pending row showing "no access
 * yet" when access was chosen — visible, and not a security question.
 */
export function pendingGrantKey(email: string): string {
  return `pending-invite:${(email ?? "").trim().toLowerCase()}`;
}

/**
 * Withdraws an invitation nobody has accepted.
 *
 * A pending invitation blocks a second one to the same address, so this is the
 * only way to re-invite somebody whose first invitation was wrong — or who was
 * removed and needs adding back.
 *
 * POST-for-delete, matching `removeMember`: the API spells destructive member
 * operations that way.
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  await axiosClient.post(
    `Auth0/v1/tenants/current/invitations/${encodeURIComponent(
      invitationId
    )}/delete`
  );
}

export type InvitationSent = {
  email: string;

  /**
   * Auth0's id for the invitation just created, so a grant can be attached to it.
   *
   * Null on the family path, which makes an Auth0 user and a password ticket rather
   * than an organization invitation -- there is no invitation to name, and its
   * absence is not a failure.
   */
  invitation_id?: string | null;
  /** "organization" | "family" — which Auth0 mechanism ran. */
  kind: string;
  /** Whether a requested expiry was honoured; the family path has a fixed one. */
  ttl_applied: boolean;
};
